import { Injectable, inject } from '@angular/core';
import { Firestore, collection, addDoc, getDocs, query, 
         where, Timestamp, doc, updateDoc, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { HttpClient } from '@angular/common/http';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Subscriber } from '../interfaces/subscriber.interface';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

// Generate UUID v4 for device ID
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

@Injectable({
  providedIn: 'root'
})
export class NewsletterService {
  private firestore = inject(Firestore);
  private http = inject(HttpClient);
  private functions = inject(Functions);
  private subscribersCollection = collection(this.firestore, 'subscribers');

  // Get or create device ID
  private getDeviceId(): string {
    let deviceId = localStorage.getItem('device-id');
    if (!deviceId) {
      deviceId = generateUUID();
      localStorage.setItem('device-id', deviceId);
    }
    return deviceId;
  }

  /**
   * Subscribing goes through the `subscribeNewsletter` Cloud Function, which
   * rate-limits per IP and per address and never reveals whether the address
   * was already on the list. (Direct Firestore writes/queries from the browser
   * were removed together with the public `list` rule.)
   */
  async subscribe(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    const call = httpsCallable<{ email: string }, { status: string }>(this.functions, 'subscribeNewsletter');
    await call({ email: normalizedEmail });

    // Notify Django asynchronously - fire-and-forget (Firestore is the source of truth)
    this.notifyDjangoAPI(normalizedEmail).subscribe({
      error: () => {}
    });
  }

  async getAllSubscribers(): Promise<Subscriber[]> {
    try {
      const snapshot = await getDocs(this.subscribersCollection);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data['email'],
          subscribedAt: data['subscribedAt'].toDate(),
          active: data['active']
        } as Subscriber;
      });
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Real-time listener: every subscriber record — for the admin dashboard.
   * Mirrors the listenToAllEvents()/listenToAllPlaylists()/listenToAllNews()
   * pattern. Previously admin-dashboard.component.ts built this listener
   * inline, reaching into this service's private `firestore` field via
   * bracket-notation (this.newsletterService['firestore']) to do it.
   */
  listenToAllSubscribers(callback: (subscribers: Subscriber[]) => void): Unsubscribe {
    return onSnapshot(this.subscribersCollection,
      (snapshot) => {
        callback(snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            email: data['email'],
            subscribedAt: data['subscribedAt']?.toDate() || new Date(),
            active: data['active'] !== undefined ? data['active'] : true,
            pendingUnsubscribe: data['pendingUnsubscribe'] || false
          } as Subscriber;
        }));
      }
    );
  }

  async getActiveSubscribers(): Promise<Subscriber[]> {
    try {
      const q = query(this.subscribersCollection, where('active', '==', true));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data['email'],
          subscribedAt: data['subscribedAt'].toDate(),
          active: data['active'],
          pendingUnsubscribe: data['pendingUnsubscribe'] || false
        } as Subscriber;
      });
    } catch (error: any) {
      throw error;
    }
  }

  async getPendingUnsubscribes(): Promise<Subscriber[]> {
    try {
      const q = query(
        this.subscribersCollection, 
        where('active', '==', true),
        where('pendingUnsubscribe', '==', true)
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data['email'],
          subscribedAt: data['subscribedAt'].toDate(),
          active: data['active'],
          pendingUnsubscribe: data['pendingUnsubscribe']
        } as Subscriber;
      });
    } catch (error: any) {
      throw error;
    }
  }

  /** Real-time counterpart to getPendingUnsubscribes() — same query, live. */
  listenToPendingUnsubscribes(callback: (subscribers: Subscriber[]) => void): Unsubscribe {
    const q = query(
      this.subscribersCollection,
      where('active', '==', true),
      where('pendingUnsubscribe', '==', true)
    );
    return onSnapshot(q,
      (snapshot) => {
        callback(snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            email: data['email'],
            subscribedAt: data['subscribedAt']?.toDate() || new Date(),
            active: data['active'],
            pendingUnsubscribe: data['pendingUnsubscribe']
          } as Subscriber;
        }));
      }
    );
  }

  /** Rate-limited server-side; same response whether or not the address exists. */
  async requestUnsubscribe(email: string): Promise<void> {
    const call = httpsCallable<{ email: string }, { status: string }>(this.functions, 'requestUnsubscribe');
    await call({ email: email.trim().toLowerCase() });
  }

  async unsubscribe(subscriberId: string): Promise<void> {
    try {
      const subscriberDoc = doc(this.firestore, 'subscribers', subscriberId);
      await updateDoc(subscriberDoc, { active: false });
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Clean up duplicate subscriber records
   * Keeps only the most recent active record or oldest record if all inactive
   */
  async cleanupDuplicates(email: string): Promise<void> {
    try {
      const q = query(this.subscribersCollection, where('email', '==', email));
      const snapshot = await getDocs(q);
      
      if (snapshot.docs.length > 1) {
        // Sort by subscribedAt, keep the most recent one
        const sortedDocs = snapshot.docs.sort((a, b) => {
          const aTime = a.data()['subscribedAt']?.toMillis() || 0;
          const bTime = b.data()['subscribedAt']?.toMillis() || 0;
          return bTime - aTime; // Most recent first
        });
        
        // Keep the first (most recent), deactivate the rest
        for (let i = 1; i < sortedDocs.length; i++) {
          const duplicateRef = doc(this.firestore, 'subscribers', sortedDocs[i].id);
          await updateDoc(duplicateRef, { active: false });
        }
      }
    } catch (error: any) {
    }
  }

  async deleteSubscriber(subscriberId: string, email: string): Promise<void> {
    try {
      // Delete from Firestore
      const subscriberDoc = doc(this.firestore, 'subscribers', subscriberId);
      await updateDoc(subscriberDoc, { active: false });
      
      // Notify Django API asynchronously to remove from mailing list
      // Fire-and-forget: Django API is not blocking; Firestore is the source of truth
      const deviceId = this.getDeviceId();
      const deleteUrl = `${environment.djangoApiUrl}/subscribers/${email}/`;
      
      this.http.delete(deleteUrl, {
        headers: {
          'X-Device-ID': deviceId
        }
      }).subscribe({
        error: () => {}
      });
    } catch (error: any) {
      throw error;
    }
  }

  private notifyDjangoAPI(email: string): Observable<any> {
    const deviceId = this.getDeviceId();
    return this.http.post(
      `${environment.djangoApiUrl}/subscribers/`,
      { email },
      {
        headers: {
          'X-Device-ID': deviceId
        }
      }
    );
  }
}